/**
 * dashboardEditChatSlice.ts — Redux state for the dashboard edit-mode chat session.
 *
 * Tracks the lifecycle of a single edit session opened by the user via
 * the "Ask TCS AI" overlay. The slice is intentionally keyed to a single
 * active session per dashboard view — opening the overlay for a different
 * dashboard calls clearEditSession to reset.
 *
 * State responsibilities:
 *  - Session identity: sessionId, the dashboard it belongs to (dashboardId).
 *  - FSM mirror: flowState + flowContext (from bootstrap + send-message responses).
 *  - Chat thread: messages (edit-era only — distinct from create-era chatSession).
 *  - Loading / error state: isBootstrapping for the bootstrap call;
 *    isSending for an in-flight message send.
 *
 * Chat-driven confirmation pivot (2026-05-11):
 *  - `pendingMutation`, `isApplying`, `isCancelling` were removed. The FE
 *    no longer renders the Apply/Cancel card — the BE-side AI interprets
 *    the user's chat reply and routes via the new
 *    `confirm_pending_mutation` / `cancel_pending_mutation` tools.
 *  - Server-side gate is unchanged: `flow_context.pendingMutation` still
 *    exists on the BE; the FE just doesn't mirror it any more.
 *
 * Persistence rule (TDD §3.7.3 + AC #6):
 *   Closing the overlay does NOT clear Redux state. The session persists
 *   in-memory until the user navigates to a different dashboard. Navigating
 *   away fires clearEditSession from Canvas. The backend archives the prior
 *   session automatically on the next bootstrapEditSession call for the
 *   same (user, dashboard) pair.
 *
 * Era: dashboard-era only. No workflow-era fields.
 *
 * TDD ref: plans/2026-05-07-edit-dashboard-via-ai-tdd.md §3.7.2
 */

import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '@/redux'
import type {
  EditFlowState,
  EditModeFlowContext,
} from '@/types/dashboardEditChat.types'
import type { ChatMessage } from '@/types/Dashboards.types'

// ─── State shape ──────────────────────────────────────────────────────────────

export interface DashboardEditChatState {
  /**
   * The dashboard this edit session belongs to.
   * Used by Canvas to detect navigation away and call clearEditSession.
   */
  dashboardId: string | null

  /**
   * The active edit session id, returned by the bootstrap endpoint.
   * null until a successful bootstrap or when the session is cleared.
   */
  sessionId: string | null

  /**
   * FSM state returned by the bootstrap and send-message endpoints.
   * null until bootstrap completes successfully.
   */
  flowState: EditFlowState | null

  /**
   * Full edit-mode flow context from the bootstrap response.
   * Carries panelSnapshot, selectedDomainIds, domainFilterValues, etc.
   * Updated on each send-message response that carries flowContext.
   */
  flowContext: EditModeFlowContext | null

  /**
   * Edit-mode chat thread. Populated by `sendEditChatMessageThunk` — one
   * entry per user message and one per assistant reply. Lives separately from
   * `chatSession.messages` (the create-mode thread) per the era-axiom rule:
   * create-era and edit-era state stays distinct.
   *
   * ChatPanel reads both arrays and renders them merged in render order
   * (creation thread first, then edit thread).
   */
  messages: ChatMessage[]

  /**
   * Whether a `sendEditChatMessageThunk` is in-flight. Drives the input
   * disabled-state and the "AI is thinking" affordance.
   */
  isSending: boolean

  /**
   * Whether the bootstrap call is in-flight.
   * Drives the loading state in the overlay while the session is being opened.
   */
  isBootstrapping: boolean

  /**
   * Error message from the most recent failed operation.
   * null when no error is active.
   */
  error: string | null
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: DashboardEditChatState = {
  dashboardId: null,
  sessionId: null,
  flowState: null,
  flowContext: null,
  messages: [],
  isSending: false,
  isBootstrapping: false,
  error: null,
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const dashboardEditChatSlice = createSlice({
  name: 'dashboardEditChat',
  initialState,
  reducers: {
    /**
     * Called when the bootstrap call starts (before the API request).
     * Sets isBootstrapping and records the dashboardId being bootstrapped.
     */
    setBootstrapping(
      state,
      action: PayloadAction<{ dashboardId: string }>
    ) {
      state.isBootstrapping = true
      state.error = null
      state.dashboardId = action.payload.dashboardId
    },

    /**
     * Called on successful bootstrap response with no prior chat history.
     *
     * Use hydrateEditSession instead when restoring a prior session (refresh
     * path) — it additionally accepts a messages array.
     */
    setSession(
      state,
      action: PayloadAction<{
        sessionId: string
        flowState: EditFlowState
        flowContext: EditModeFlowContext
      }>
    ) {
      state.isBootstrapping = false
      state.sessionId = action.payload.sessionId
      state.flowState = action.payload.flowState
      state.flowContext = action.payload.flowContext
      state.error = null
    },

    /**
     * Called on successful bootstrap response when the BE has forwarded messages
     * from a prior archived session (browser refresh path).
     *
     * Identical to setSession but also populates the messages array so the
     * chat history survives a page reload. When priorMessages is empty,
     * behaviour is identical to setSession.
     *
     * Chat-driven confirmation pivot (2026-05-11): no longer mirrors
     * `flowContext.pendingMutation` on the slice — the FE has no card to
     * render and the BE-side AI re-reads the staged mutation from
     * `flow_context` on the next turn.
     */
    hydrateEditSession(
      state,
      action: PayloadAction<{
        sessionId: string
        flowState: EditFlowState
        flowContext: EditModeFlowContext
        messages: ChatMessage[]
      }>
    ) {
      state.isBootstrapping = false
      state.sessionId = action.payload.sessionId
      state.flowState = action.payload.flowState
      state.flowContext = action.payload.flowContext
      state.messages = action.payload.messages
      state.error = null
    },

    /**
     * Updates flowState and flowContext after a chat message response that
     * carries them (mutation_proposed / mutation_applied / mutation_cancelled).
     */
    updateSessionFromMessageResponse(
      state,
      action: PayloadAction<{
        flowState: EditFlowState
        flowContext: EditModeFlowContext
      }>
    ) {
      state.flowState = action.payload.flowState
      state.flowContext = action.payload.flowContext
      state.error = null
    },

    /**
     * Appends a single message to the edit-mode chat thread. Used by
     * `sendEditChatMessageThunk` to render the optimistic user bubble and
     * the assistant reply.
     */
    appendEditMessage(state, action: PayloadAction<ChatMessage>) {
      state.messages.push(action.payload)
    },

    setIsSending(state, action: PayloadAction<boolean>) {
      state.isSending = action.payload
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
      state.isBootstrapping = false
    },

    /**
     * Resets all state to initial values. Called by Canvas when the user
     * navigates from one dashboard to another so the next bootstrap is clean.
     *
     * Per AC #6: closing the overlay does NOT call clearEditSession — only
     * navigation away from the dashboard page does.
     */
    clearEditSession() {
      return initialState
    },
  },
})

// ─── Exports ─────────────────────────────────────────────────────────────────

export const dashboardEditChatReducer = dashboardEditChatSlice.reducer
export const dashboardEditChatActions = dashboardEditChatSlice.actions

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectEditSessionId = (state: RootState): string | null =>
  state.dashboardEditChat.sessionId

export const selectEditFlowState = (state: RootState): EditFlowState | null =>
  state.dashboardEditChat.flowState

export const selectEditFlowContext = (state: RootState): EditModeFlowContext | null =>
  state.dashboardEditChat.flowContext

export const selectIsBootstrapping = (state: RootState): boolean =>
  state.dashboardEditChat.isBootstrapping

export const selectEditChatError = (state: RootState): string | null =>
  state.dashboardEditChat.error

export const selectEditSessionDashboardId = (state: RootState): string | null =>
  state.dashboardEditChat.dashboardId

export const selectEditMessages = (state: RootState): ChatMessage[] =>
  state.dashboardEditChat.messages

export const selectEditIsSending = (state: RootState): boolean =>
  state.dashboardEditChat.isSending

export default dashboardEditChatSlice
